import { Controller } from "@hotwired/stimulus"

// Sprint 19 (04/06/2026) — Filtrage dynamique du select dendreo_product_id
// dans le form deal B2C. Quand l'utilisateur change b2c_category ou
// training_interest, on fetch la liste filtrée depuis
// /dendreo_products/search (avec params category + interest + by_usage)
// et on repopule le select avec :
//   - Modules suggérés (top 5 par usage) en premier (optgroup)
//   - Autres modules de la catégorie (alphabétique)
//
// Réduit la charge cognitive sur ~500 modules → ~20 pertinents.
export default class extends Controller {
  static targets = ["categorySelect", "interestSelect", "moduleSelect", "hint"]
  static values = { searchUrl: String }

  // Le binding event ↔ refresh est fait via Stimulus data-action attribute
  // dans le form (`change->deal-b2c-dendreo-filter#refresh`). Pas besoin
  // d'addEventListener manuel ici.

  async refresh() {
    const category = this.categorySelectTarget?.value || ""
    const interest = this.interestSelectTarget?.value || ""
    const select = this.moduleSelectTarget
    if (!select) return

    const currentValue = select.value

    if (!category) {
      this.showHint("Choisis d'abord une catégorie pour filtrer les modules.")
      return
    }

    this.showHint("Chargement des modules…")

    try {
      const params = new URLSearchParams({ by_usage: "true" })
      if (category) params.set("category", category)
      if (interest) params.set("interest", interest)
      const url = `${this.searchUrlValue}?${params.toString()}`
      const resp = await fetch(url, { headers: { Accept: "application/json" } })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const products = await resp.json()

      this.repopulate(select, products, currentValue)
      const topUsage = products.filter(p => p.usage > 0).length
      this.showHint(
        products.length === 0
          ? "Aucun module Dendreo ne correspond à ce filtre — choisis un autre intérêt formation."
          : `${products.length} modules · ${topUsage} déjà utilisés (en haut)`
      )
    } catch (err) {
      this.showHint(`Erreur chargement modules : ${err.message}`)
    }
  }

  repopulate(select, products, currentValue) {
    while (select.firstChild) select.removeChild(select.firstChild)

    const blank = document.createElement("option")
    blank.value = ""
    blank.textContent = "Sélectionner (auto-mappé si vide)"
    select.appendChild(blank)

    const used = products.filter(p => p.usage && p.usage > 0).slice(0, 5)
    const others = products.filter(p => !used.includes(p))

    if (used.length > 0) {
      const groupUsed = document.createElement("optgroup")
      groupUsed.label = "Modules les + utilisés"
      used.forEach(p => groupUsed.appendChild(this.option(p)))
      select.appendChild(groupUsed)
    }

    if (others.length > 0) {
      const groupOthers = document.createElement("optgroup")
      groupOthers.label = used.length > 0 ? "Autres modules" : "Modules"
      others.forEach(p => groupOthers.appendChild(this.option(p)))
      select.appendChild(groupOthers)
    }

    if (currentValue && [...select.options].some(o => o.value === currentValue)) {
      select.value = currentValue
    }
  }

  option(product) {
    const opt = document.createElement("option")
    opt.value = product.id
    opt.textContent = product.usage > 0
      ? `${product.name} (${product.usage}×)`
      : product.name
    return opt
  }

  showHint(text) {
    if (this.hasHintTarget) this.hintTarget.textContent = text
  }
}
